import { laravelGraphqlEndpoint } from '@/globals/globals'
import { UserContext } from '@/lib/UserContext'
import { MemberProjectLike } from '@/types/memberProjectLike'
import { Box, Flex, Link, Text } from '@chakra-ui/react'
import request, { gql } from 'graphql-request'
import { useContext, useEffect, useState } from 'react'
import { HeartFilledIcon } from './icons/HeartFilledIcon'
import { HeartOutlineIcon } from './icons/HeartOutlineIcon'

type Props = {
  projectUuid: string
}

const getData = async (projectId: string) => {
  return await request(
    laravelGraphqlEndpoint,
    gql`
      query ($uuid: String!) {
        memberProjectByUUID(uuid: $uuid) {
          likes {
            id
            user_uuid
            project_uuid
          }
        }
      }
    `,
    {
      uuid: projectId,
    }
  )
}

export const LikeButton = ({ projectUuid }: Props) => {
  const user = useContext(UserContext)
  const [likes, setLikes] = useState<MemberProjectLike[]>([])
  const [isLiked, setIsLiked] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const userData = user()

  useEffect(() => {
    getData(projectUuid).then(data => {
      setLikes(data.memberProjectByUUID.likes)
      setLoaded(true)
      if (!userData) return
      data.memberProjectByUUID.likes.some(
        (like: MemberProjectLike) => like.user_uuid === userData.uuid
      )
        ? setIsLiked(true)
        : setIsLiked(false)
    })
  }, [projectUuid, userData])

  const addLike = () => {
    setIsLiked(true)
    setLikes([
      ...likes,
      {
        id: likes.length + 1,
        user_uuid: userData?.uuid,
        project_uuid: projectUuid,
      },
    ])
    const query = gql`
      mutation LikeQuery($project_uuid: ID!, $user_uuid: ID!) {
        createMemberProjectLike(
          project_uuid: $project_uuid
          user_uuid: $user_uuid
        ) {
          project {
            uuid
            likes {
              id
              user_uuid
              project_uuid
            }
          }
        }
      }
    `
    request(laravelGraphqlEndpoint, query, {
      project_uuid: projectUuid,
      user_uuid: userData?.uuid,
    })
  }

  useEffect(() => {
    likes.some(like => like.user_uuid === userData?.uuid)
      ? setIsLiked(true)
      : setIsLiked(false)
  }, [userData, likes])

  const removeLike = () => {
    setLikes(likes.filter(like => like.user_uuid !== userData?.uuid))
    setIsLiked(false)
    const query = gql`
      mutation LikeQuery($id: ID!) {
        deleteMemberProjectLike(id: $id) {
          project {
            uuid
          }
        }
      }
    `
    request(laravelGraphqlEndpoint, query, {
      id: likes.find(like => like.user_uuid === userData?.uuid)?.id,
    })
  }

  return (
    <Box textAlign='center'>
      <Flex alignItems='center' gap={2} justifyContent='center'>
        {isLiked ? (
          <Link onClick={removeLike} color='red.400' _hover={{ color: 'cyan' }}>
            <HeartFilledIcon fill='red.400' width={10} height={10} />
          </Link>
        ) : userData ? (
          <Link onClick={addLike} color='cyan' _hover={{ color: 'red.400' }}>
            <HeartOutlineIcon
              fill='cyan'
              _hover={{ fill: 'red.400' }}
              width={10}
              height={10}
            />
          </Link>
        ) : (
          <HeartFilledIcon fill='cyan' width={10} height={10} />
        )}
        <Text
          opacity={loaded ? 1 : 0}
          fontSize={24}
          fontWeight='bold'
          minW={4}
          textAlign='right'>
          {likes.length}
        </Text>
      </Flex>
    </Box>
  )
}
